import React, { useEffect, useState } from 'react';
import {
  Box, Text, Badge, Select, SimpleGrid, Image, Flex, Icon, IconButton, Link,
} from '@chakra-ui/react';
import { FiFileText, FiTrash2, FiExternalLink } from 'react-icons/fi';
import { listarExpedientes, fileUrl } from '../../api/immApi';
import type { ImmExpediente, ImmArchivo } from '../../api/immApi';

/** Color institucional del sistema. */
export const IMM_COLOR = '#5b182b';
export const IMM_COLOR_HOVER = '#691f30';

/** Formatea una fecha ISO a dd/mm/aaaa. */
export const fmt = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

/** Formatea fecha + hora. */
export const fmtDateTime = (v?: string | null) =>
  v ? new Date(v).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

/** Nombre completo del expediente (para vistas de detalle, NO listados sensibles). */
export const nombreCompleto = (e?: Partial<ImmExpediente> | null) =>
  e ? [e.nombre, e.apPaterno, e.apMaterno].filter(Boolean).join(' ') : '—';

/** Iniciales enmascaradas para listados sensibles (p. ej. "María G. R."). */
export const nombreEnmascarado = (e?: Partial<ImmExpediente> | null) => {
  if (!e) return '—';
  const ini = (s?: string | null) => (s ? `${s.charAt(0).toUpperCase()}.` : '');
  return [e.nombre, ini(e.apPaterno), ini(e.apMaterno)].filter(Boolean).join(' ');
};

/** Título de sección con la línea inferior institucional. */
export const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Box borderBottom="2px solid" borderColor={IMM_COLOR} pb={1} mb={1} mt={3}>
    <Text fontSize="xs" fontWeight="bold" color={IMM_COLOR} textTransform="uppercase" letterSpacing="wide">
      {children}
    </Text>
  </Box>
);

/** Campo de solo lectura etiqueta/valor. */
export const Field = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Box>
    <Text fontSize="xs" color="gray.500" fontWeight="bold">{label}</Text>
    <Text fontSize="sm" whiteSpace="pre-wrap">{value || '—'}</Text>
  </Box>
);

// ---- Mapas de color por estatus / clasificación ----

export const ESTATUS_COLOR: Record<string, string> = {
  Activo: 'green', Inactivo: 'gray',
  Abierto: 'orange', EnSeguimiento: 'blue', Cerrado: 'green', Anulado: 'red',
  Vigente: 'green', Vencida: 'orange', Cancelada: 'red',
  Recibida: 'orange', Vinculada: 'blue', Atendida: 'green', Cerrada: 'gray',
  Programada: 'blue', Reagendada: 'purple',
  Generada: 'blue', Registrado: 'blue',
  Programado: 'blue', Finalizado: 'green',
  Inscrita: 'blue', Confirmada: 'teal', Asistio: 'green', NoAsistio: 'red', Cancelo: 'gray',
};

export const NIVEL_RIESGO_COLOR: Record<string, string> = {
  Bajo: 'green', Medio: 'orange', Alto: 'red',
};

/** Etiqueta legible para tipo de decisión. */
export const TIPO_DECISION_LABEL: Record<string, string> = {
  DeseaDenunciar: 'Desea denunciar',
  NoDeseaDenunciar: 'No desea denunciar',
  SoloOrientacion: 'Solo orientación',
  Acompanamiento: 'Acompañamiento',
  Canalizacion: 'Canalización',
  Seguimiento: 'Seguimiento',
};

/** Etiqueta legible para el tipo de evento de la línea de tiempo. */
export const LINEA_TIEMPO_LABEL: Record<string, string> = {
  Atencion: 'Atención',
  Caso: 'Caso',
  Canalizacion: 'Canalización',
  Decision: 'Decisión',
  OrdenProteccion: 'Orden de protección',
  CitaPsicologica: 'Cita psicológica',
  Constancia: 'Constancia',
};

export const LINEA_TIEMPO_COLOR: Record<string, string> = {
  Atencion: 'blue',
  Caso: 'red',
  Canalizacion: 'purple',
  Decision: 'teal',
  OrdenProteccion: 'orange',
  CitaPsicologica: 'pink',
  Constancia: 'green',
};

export const EstatusBadge = ({ value }: { value?: string }) => (
  <Badge colorScheme={value ? (ESTATUS_COLOR[value] || 'gray') : 'gray'} fontSize="xs" px={2} py={0.5} borderRadius="full">
    {value || '—'}
  </Badge>
);

/** Determina si un archivo es una imagen (por mimeType o extensión). */
export const esImagen = (a?: Partial<ImmArchivo> | null) => {
  if (!a) return false;
  if (a.mimeType) return a.mimeType.startsWith('image/');
  const nombre = (a.nombreOriginal || a.rutaArchivo || '').toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(nombre);
};

/**
 * Galería de evidencias/documentos en modo Ver: miniaturas clicables.
 * Las imágenes se muestran como thumbnail y abren en pestaña nueva; los PDF/otros
 * archivos muestran un ícono con enlace "Abrir". Opcionalmente permite eliminar.
 */
export const GaleriaEvidencias = ({
  archivos, onEliminar,
}: {
  archivos?: ImmArchivo[] | null;
  onEliminar?: (a: ImmArchivo) => void;
}) => {
  const items = (archivos || []).filter((a) => a && a.rutaArchivo);
  if (!items.length) {
    return <Text color="gray.400" fontSize="sm" py={2}>Sin archivos cargados.</Text>;
  }
  return (
    <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} spacing={3}>
      {items.map((a) => {
        const url = fileUrl(a.rutaArchivo);
        const img = esImagen(a);
        return (
          <Box key={a.id} borderWidth="1px" borderColor="gray.200" borderRadius="md"
            overflow="hidden" bg="white" boxShadow="sm" position="relative"
            transition="all .15s" _hover={{ boxShadow: 'md', borderColor: IMM_COLOR }}>
            <Link href={url} isExternal _hover={{ textDecoration: 'none' }} display="block">
              {img ? (
                <Image src={url} alt={a.nombreOriginal || 'evidencia'}
                  objectFit="cover" w="100%" h="120px" bg="gray.50" />
              ) : (
                <Flex direction="column" align="center" justify="center" h="120px" bg="gray.50">
                  <Icon as={FiFileText} boxSize={9} color={IMM_COLOR} />
                  <Flex align="center" mt={1} color={IMM_COLOR}>
                    <Icon as={FiExternalLink} boxSize={3} mr={1} />
                    <Text fontSize="2xs" fontWeight="bold">Abrir</Text>
                  </Flex>
                </Flex>
              )}
            </Link>
            <Box px={2} py={1.5}>
              {a.tipo && <Badge colorScheme="purple" fontSize="2xs" mb={0.5}>{a.tipo}</Badge>}
              <Text fontSize="2xs" color="gray.600" noOfLines={1} title={a.nombreOriginal || ''}>
                {a.nombreOriginal || 'archivo'}
              </Text>
            </Box>
            {onEliminar && (
              <IconButton aria-label="Eliminar archivo" icon={<FiTrash2 />} size="xs"
                colorScheme="red" position="absolute" top={1} right={1}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEliminar(a); }} />
            )}
          </Box>
        );
      })}
    </SimpleGrid>
  );
};

/**
 * Selector reutilizable de expediente (folio + nombre). Usado por los formularios
 * que requieren expedienteId (atenciones, casos, canalizaciones, órdenes, etc.).
 * `isDisabled` para bloquearlo cuando el expediente ya viene fijado por contexto.
 */
export const ExpedienteSelect = ({
  value, onChange, isDisabled,
}: {
  value?: number | null;
  onChange: (id: number | undefined) => void;
  isDisabled?: boolean;
}) => {
  const [exps, setExps] = useState<ImmExpediente[]>([]);
  useEffect(() => { listarExpedientes().then(setExps).catch(() => setExps([])); }, []);
  return (
    <Select
      placeholder="Selecciona expediente"
      value={value ?? ''}
      isDisabled={isDisabled}
      onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
    >
      {exps.map((e) => (
        <option key={e.id} value={e.id}>
          {e.folio} — {[e.nombre, e.apPaterno, e.apMaterno].filter(Boolean).join(' ')}
        </option>
      ))}
    </Select>
  );
};
